import React from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import {
  FaRobot,
  FaBrain,
  FaLink,
  FaBullhorn,
  FaStore,
  FaInstagram,
  FaUsers,
  FaCheckCircle,
  FaChartPie,
  FaShieldAlt,
  FaWallet,
  FaRocket,
} from "react-icons/fa";

const influencerFeatures = [
  {
    icon: <FaRobot className="w-6 h-6" />,
    title: "Comment-to-DM Automation",
    description:
      "When someone comments your keyword on a post or reel, Infuber auto-sends them a DM with your link — with an optional follow-to-unlock gate that grows your audience.",
    color: "bg-orange-500",
  },
  {
    icon: <FaBrain className="w-6 h-6" />,
    title: "AI Comment Insights",
    description:
      "Understand your audience with Gemini-powered sentiment analysis — sentiment scores, audience persona, top themes and recommended next actions.",
    color: "bg-blue-500",
  },
  {
    icon: <FaLink className="w-6 h-6" />,
    title: "Affiliate Links & Earnings",
    description:
      "Generate tracked short links for brand campaigns, watch clicks and conversions in real time, and get paid through transparent earnings and payouts.",
    color: "bg-orange-500",
  },
  {
    icon: <FaBullhorn className="w-6 h-6" />,
    title: "Campaigns & Applications",
    description:
      "Discover brand advertising campaigns, apply in a click, and track every application and brand invitation from one dashboard.",
    color: "bg-blue-500",
  },
  {
    icon: <FaStore className="w-6 h-6" />,
    title: "Creator Profile & Packages",
    description:
      "Get a shareable profile with your link-in-bio, social stats and priced service packages for Reels, Shorts and Stories.",
    color: "bg-orange-500",
  },
  {
    icon: <FaInstagram className="w-6 h-6" />,
    title: "Instagram Insights",
    description:
      "Connect your account and sync your latest posts and reels to see likes, comments and reach at a glance.",
    color: "bg-blue-500",
  },
];

const brandFeatures = [
  {
    icon: <FaBullhorn className="w-6 h-6" />,
    title: "Campaign Builder",
    description:
      "Launch Affiliate, Hybrid or Advertising campaigns with a guided builder — commissions, landing pages, tracking and integrations included.",
    color: "bg-orange-500",
  },
  {
    icon: <FaStore className="w-6 h-6" />,
    title: "Influencer Marketplace",
    description:
      "Search a directory of creators by platform, niche, audience size, price and location, then unlock verified contact details.",
    color: "bg-blue-500",
  },
  {
    icon: <FaUsers className="w-6 h-6" />,
    title: "Outreach Pipeline",
    description:
      "Shortlist creators and move them through a Kanban pipeline — from contacted to negotiating to contracted and confirmed.",
    color: "bg-orange-500",
  },
  {
    icon: <FaRobot className="w-6 h-6" />,
    title: "Brand DM Automation",
    description:
      "Run the same comment-to-DM automation on your brand's Instagram to turn engagement into qualified leads automatically.",
    color: "bg-blue-500",
  },
  {
    icon: <FaCheckCircle className="w-6 h-6" />,
    title: "Media Approval",
    description:
      "Review creator-submitted Instagram and YouTube media and approve or reject with clear feedback before it goes live.",
    color: "bg-orange-500",
  },
  {
    icon: <FaChartPie className="w-6 h-6" />,
    title: "Analytics & Payments",
    description:
      "Track campaign performance with live charts and manage influencer payment records in one financial view.",
    color: "bg-blue-500",
  },
];

const whyChoose = [
  {
    icon: <FaShieldAlt className="w-10 h-10" />,
    title: "Secure & Role-Based",
    description:
      "Token-based authentication with dedicated, protected spaces for creators, brands and admins.",
  },
  {
    icon: <FaBrain className="w-10 h-10" />,
    title: "AI at the Core",
    description:
      "Gemini-powered comment analysis and Instagram automation do the heavy lifting for you.",
  },
  {
    icon: <FaWallet className="w-10 h-10" />,
    title: "Built-in Monetization",
    description:
      "From tracked affiliate links to earnings, payouts and service packages — getting paid is part of the platform.",
  },
  {
    icon: <FaUsers className="w-10 h-10" />,
    title: "One Platform, Two Sides",
    description:
      "Brands and creators work in the same connected ecosystem, from discovery to conversion.",
  },
];

const FeatureCard = ({ feature }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur transform transition-transform duration-300 hover:scale-[1.03]">
    <div className="flex items-center gap-3 mb-4">
      <div className={`${feature.color} w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-white`}>
        {feature.icon}
      </div>
      <h4 className="text-base md:text-lg font-semibold mb-0 text-white/95">{feature.title}</h4>
    </div>
    <p className="text-white/70 text-sm md:text-[15px] leading-relaxed">{feature.description}</p>
  </div>
);

const AboutInfuber = () => {
  return (
    <div className="bg-black text-white min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-24 pb-16 md:pt-28 md:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
          <div className="absolute -bottom-40 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-block rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-orange-400">
            About Infuber
          </span>
          <h1 className="mt-6 text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold tracking-tight text-white">
            The all-in-one platform for Instagram growth and monetization
          </h1>
          <p className="mt-6 text-sm md:text-base text-white/70 max-w-3xl mx-auto leading-relaxed">
            Infuber connects brands and creators in one ecosystem — automating Instagram
            comments and DMs, decoding audience sentiment with AI, and turning everyday
            engagement into measurable business results.
          </p>
          <div className="mt-8 flex flex-col md:flex-row flex-wrap justify-center items-center gap-4">
            <Link to="/our-work">
              <button className="bg-orange-500 md:px-6 px-4 md:py-3 py-2 text-white font-bold rounded-md w-48 hover:bg-orange-600 transition-colors">
                Explore Services
              </button>
            </Link>
            <Link to="/marketplace">
              <button className="border border-white/30 md:px-6 px-4 md:py-3 py-2 font-bold rounded-md w-48 hover:bg-white hover:text-black transition-colors">
                Browse Creators
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission / What We Do */}
      <section className="relative overflow-hidden py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-44 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="absolute -bottom-44 left-2/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white/95">What we do</h2>
            <p className="text-white/70 text-sm md:text-lg leading-relaxed">
              Infuber is a comprehensive influencer marketing and automation platform. Creators
              automate replies to their audience, grow their following with smart follow-gates,
              and earn through affiliate links, campaigns and service packages. Brands discover
              the right creators, launch affiliate, hybrid and advertising campaigns, run their
              own Instagram automations, and track every click, conversion and payout — all backed
              by AI-powered audience insights.
            </p>
          </div>
        </div>
      </section>

      {/* AI & Automation Highlight */}
      <section className="relative overflow-hidden py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-orange-500/12 blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-500 w-12 h-12 rounded-xl flex items-center justify-center text-white">
                <FaRobot className="w-6 h-6" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-0 text-white/95">Comment → DM Automation</h3>
            </div>
            <p className="text-white/70 text-sm md:text-base leading-relaxed">
              Automate a post or reel, set your keywords, and let Infuber handle the rest. The
              moment a matching comment arrives, we check whether the user follows you — non-followers
              are guided to follow first, then your link is delivered straight to their DMs. You can
              even auto-reply to the comment publicly once the DM is sent.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-500 w-12 h-12 rounded-xl flex items-center justify-center text-white">
                <FaBrain className="w-6 h-6" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-0 text-white/95">AI Audience Sentiment</h3>
            </div>
            <p className="text-white/70 text-sm md:text-base leading-relaxed">
              Powered by Google Gemini, Infuber reads through your comments and turns them into clear
              insight — positive, neutral and negative counts, an overall sentiment score, engagement
              quality, audience persona, recurring themes and recommended actions to grow faster.
            </p>
          </div>
        </div>
      </section>

      {/* For Influencers */}
      <section className="relative overflow-hidden py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-44 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="absolute -bottom-44 left-2/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 text-white/95">For Influencers</h2>
            <p className="text-white/70 max-w-2xl mx-auto text-sm md:text-lg">
              Everything a creator needs to automate engagement, grow an audience and get paid.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {influencerFeatures.map((feature) => (
              <FeatureCard key={feature.title} feature={feature} />
            ))}
          </div>
        </div>
      </section>

      {/* For Brands */}
      <section className="relative overflow-hidden py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-44 left-2/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="absolute -bottom-44 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 text-white/95">For Brands</h2>
            <p className="text-white/70 max-w-2xl mx-auto text-sm md:text-lg">
              Discover creators, launch campaigns and convert engagement — end to end.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brandFeatures.map((feature) => (
              <FeatureCard key={feature.title} feature={feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Infuber */}
      <section className="relative overflow-hidden py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-44 left-1/2 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-orange-500/12 blur-3xl" />
          <div className="absolute -bottom-44 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-white/95">Why choose Infuber</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 text-center shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur transition-transform duration-300 hover:scale-[1.03]"
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/5 text-orange-500">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-white/95">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-44 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
          <div className="absolute -bottom-44 left-2/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto text-center">
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-500 text-white">
            <FaRocket className="w-6 h-6" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white/95">
            Ready to grow with Infuber?
          </h2>
          <p className="text-white/70 text-sm md:text-lg mb-8 max-w-2xl mx-auto">
            Whether you're a brand looking to convert engagement or a creator ready to automate and
            earn, Infuber gives you the tools to do it from one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/influencer/login">
              <button className="bg-orange-500 text-white px-8 py-3 rounded-md font-semibold hover:bg-orange-600 transition-colors">
                Get Started as a Creator 
              </button>
            </Link>
            <Link to="/company/login">
              <button className="border border-white/30 px-8 py-3 rounded-md font-semibold hover:bg-white hover:text-black transition-colors">
                I'm a Brand or Agency
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutInfuber;
